import type { Key, Ref } from 'react';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Observable, Subject, takeUntil } from 'rxjs';
import * as yup from 'yup';
import type {
  JsonDataWrapper,
  OdinDataRetrieval,
  OdinDataUpdate,
} from '../../../common/data-retrieval/data-retrieval-interfaces';
import { OdinDataSender } from '../../../common/data-retrieval/odin-data-sender';
import type { ModalDialogButtonSetting } from '../../common-interfaces';
import type { DynamicFormRef } from '../../dynamic-form/dynamic-form.component';
import DynamicForm from '../../dynamic-form/dynamic-form.component';
import { DynamicFormRequiredType } from '../../dynamic-form/dynamic-form.enums';
import type { DynamicFormField, DynamicFormSettings } from '../../dynamic-form/dynamic-form.interfaces';
import type { JsonData, JsonDataItem } from '../../generic-types';
import type { DialogProps, DialogRef } from '../../modal-dialog/dialog.component';
import Dialog from '../../modal-dialog/dialog.component';

export interface AddNewModalProps extends Omit<DialogProps, 'children' | 'buttons' | 'defaultValue'> {
  ref?: Ref<DialogRef>;
  formId?: string;
  fieldId?: Key;
  textField: string;
  valueField: string;
  placeholder?: string;
  data?: JsonData | (OdinDataRetrieval & OdinDataUpdate<JsonDataWrapper>);
  defaultValue?: string | JsonDataItem;
  buttonText?: string;
  fieldCaption?: string;
  helpText?: string;
  onItemCreated?: (value: JsonDataItem) => void;
  extraFields?: Array<DynamicFormField>;
  validateValue?: (value: string) => string | undefined;
}

function AddNewModal(props: AddNewModalProps) {
  const modalProps: Omit<DialogProps, 'children' | 'buttons' | 'defaultValue'> = props;
  const {
    ref,
    formId = 'addNewModal',
    textField,
    valueField,
    data,
    defaultValue,
    fieldId,
    buttonText,
    fieldCaption,
    placeholder,
    helpText,
    visible,
    onItemCreated,
    extraFields,
    validateValue,
  } = props;
  const { t } = useTranslation();

  const dynamicFormRef = useRef<DynamicFormRef>(null);

  const button = useMemo<ModalDialogButtonSetting>(
    () => ({
      name: 'formSubmitButton',
      text: buttonText ?? t('apply'),
      onClick: () => dynamicFormRef.current?.submitForm(),
    }),
    [buttonText, t]
  );

  const dataCreateSender = useRef<OdinDataSender<JsonDataWrapper> | undefined>(undefined);
  const destroySubject = useRef<Subject<void> | undefined>(undefined);

  const formSettings = useMemo<DynamicFormSettings>(
    () => ({
      id: formId,
      caption: '',
      fields: [
        {
          id: 'value',
          fieldType: 'TEXTFIELD',
          fieldName: 'value',
          caption: fieldCaption ?? t('new-item-name'),
          position: 1,
          required: DynamicFormRequiredType.True,
          validationFunc: yup.string().required(t('insert-value')),
          permissions: { edit: true },
          hintMessage: placeholder,
          dynamicProperties: {
            enableDebounce: true,
          },
        },
        ...(extraFields ?? []),
      ],
    }),
    [formId, fieldCaption, placeholder, t, extraFields]
  );

  const defaultFormData = useMemo(
    () =>
      typeof defaultValue === 'object' && defaultValue !== null ? { ...defaultValue } : { value: defaultValue ?? '' },
    [defaultValue, visible]
  );

  useEffect(() => {
    if (visible) {
      dynamicFormRef.current?.resetFormState(defaultFormData);
      dynamicFormRef.current?.setFieldError('value', {});
    }
  }, [visible, defaultFormData]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: Mount-only effect: onItemCreated is stable
  useEffect(() => {
    destroySubject.current = new Subject<void>();

    new Observable<JsonDataWrapper>((subscriber) => {
      dataCreateSender.current = new OdinDataSender(subscriber);
    })
      .pipe(takeUntil(destroySubject.current))
      .subscribe({
        next: (dataWrapper) => {
          const returnValue = dataWrapper.data?.[0];
          if (dataWrapper.customProperties?.isError) {
            // biome-ignore lint/suspicious/noConsole: no telemetry path in this package yet. Tracked by MYOSH-15334
            console.error(`There was an error creating new item ${dataWrapper.customProperties.error}`);
          } else if (returnValue) {
            onItemCreated?.(returnValue);
          }
        },
        // biome-ignore lint/suspicious/noConsole: no telemetry path in this package yet. Tracked by MYOSH-15334
        error: (error) => console.error(error),
      });

    return () => {
      destroySubject.current?.next();
      destroySubject.current?.complete();
    };
  }, []);

  const onDialogClose = useCallback(() => {
    props.onClose?.();
  }, [props]);

  const onFormSubmit = useCallback(
    (formData: JsonDataItem) => {
      const value = formData.value as string;
      const { value: _value, ...extraFieldValues } = formData;
      const addedValue: JsonDataItem = {
        [valueField]: value,
        [textField]: value,
        ...extraFieldValues,
      };

      if (validateValue) {
        const error = validateValue(value);
        if (error) {
          dynamicFormRef.current?.setFieldError('value', { message: error });
          return;
        }
      }

      if (data && !Array.isArray(data) && data.saveData && dataCreateSender.current) {
        data.saveData(addedValue, dataCreateSender.current, {
          fieldId,
          fieldType: 'CUSTOM',
          customFieldType: 'ADDNEWMODAL',
        });
      }

      onDialogClose();
    },
    [onDialogClose, valueField, textField, data, fieldId, validateValue]
  );

  return (
    <>
      {modalProps.visible && (
        <Dialog {...modalProps} ref={ref} onClose={onDialogClose} buttons={[button]} containerClassName="md:w-[340px]">
          <DynamicForm
            ref={dynamicFormRef}
            dynamicFormId={formId}
            settings={formSettings}
            data={defaultFormData}
            showSubmitButtons={false}
            onSubmit={onFormSubmit}
            validationEnabled={true}
            formMode="all"
          />
          {helpText && <span className="text-primary">{helpText}</span>}
        </Dialog>
      )}
    </>
  );
}

export default AddNewModal;
